import { defineStore } from 'pinia';

// Temporary mock
import transactionsMock from '@/assets/json/mocks/transactions.json';

export const useWalletStore = defineStore('wallet', () => {
  const { ApiService } = useApiService();
  const authStore = useAuthStore();
  const userStore = useUserDataStore();

  const { defaultSelectedDepositMethod, defaultSelectedWithdrawMethod } =
    usePayments();

  const userIsAuthenticated = computed(() => {
    return authStore.isAuthenticated;
  });

  const currentUserID = computed(() => {
    return userStore.user?.id;
  });

  const loadingUserProfile = computed(() => {
    return userStore.isLoading;
  });

  const loadedUserProfile = computed(() => {
    return userStore.loadedUser;
  });

  const realWallet = ref({
    balance: 0,
    credit: 0,
    walletId: 0,
    creditAmount: 0,
    withdrawEnabled: 0,
    minWithdrawAmount: 0,
    maxWithdrawAmount: 0,
    hasWalletDeposit: false,
    withdraw_enable_now: false,
    withdraw_next_date: '',
  });

  const bonusWallet = ref({
    credit: 0,
    credit_hold: 0,
    sports_amount_hold: 0,
    casino_amount_hold: 0,
    expiry_datetime: '',
  });

  const cashbackWallet = ref({
    credit: 0,
    expires_at: '',
  });

  const transactions = ref<ITransactionResponse>({} as ITransactionResponse);
  const isLoading = ref(false);
  // `isLoading` comeca false e os saldos comecam zerados, entao ele nao
  // distingue "carteira ainda nao buscada" de "carteira buscada e zerada".
  // Quem precisa DECIDIR algo com base no saldo (ex.: mostrar ou nao a escolha
  // de carteira do Jogar com Bonus) precisa dessa diferenca.
  const hasLoadedWallet = ref(false);
  const rolloverAccomplished = ref(false);
  const rollover = ref<IRollover>({
    success: false,
  });

  // Mesma razao do `hasLoadedWallet`: `rollover.success` comeca false e nao
  // distingue "nao buscado" de "buscado e sem bonus ativo".
  const hasLoadedRollover = ref(false);

  function $reset() {
    // Mesma razão do resto do `$reset`: sem zerar a assinatura, o primeiro
    // `getRolloverData` do próximo usuário compararia com o rollover do
    // anterior e poderia pular o `checkRollover`.
    lastRolloverSignature = undefined;

    realWallet.value.balance = 0;
    realWallet.value.credit = 0;
    realWallet.value.walletId = 0;
    realWallet.value.withdrawEnabled = 0;
    realWallet.value.minWithdrawAmount = 0;
    realWallet.value.maxWithdrawAmount = 0;
    realWallet.value.hasWalletDeposit = false;
    bonusWallet.value.credit = 0;
    bonusWallet.value.credit_hold = 0;
    bonusWallet.value.sports_amount_hold = 0;
    bonusWallet.value.casino_amount_hold = 0;
    bonusWallet.value.expiry_datetime = '';
    currentDepositMethod.value = defaultSelectedDepositMethod.value;
    // Voltam a "nao buscadas": os saldos acabaram de ser zerados, entao quem
    // decide algo pelo saldo precisa buscar de novo. Sem isto, trocar de conta
    // na mesma aba deixaria a flag ligada com saldo zerado e o Jogar com Bonus
    // nunca apareceria para o novo usuario — e o rollover exibido seria o do
    // usuario anterior.
    hasLoadedWallet.value = false;
    hasLoadedRollover.value = false;
  }

  async function getWallet() {
    if (userIsAuthenticated.value) {
      let withCurrentUser = '';

      if (
        (!currentUserID.value && loadingUserProfile.value === false) ||
        !loadedUserProfile.value
      ) {
        userStore.setLoading(true);
        withCurrentUser = '?withCurrentUser=true';
      }

      isLoading.value = true;

      return await ApiService.get(`/users/wallet${withCurrentUser}`)
        .then((res: any) => {
          try {
            if (withCurrentUser) {
              userStore.setLoading(false);
            }
          } catch (ex) {}

          const data: WalletResponseApi = res.data;

          const creditAmount = data.credit < 0 ? 0 : data.credit;

          // Setting real wallet
          realWallet.value = {
            balance: data.balance / 100,
            credit: creditAmount / 100,
            walletId: data.id,
            creditAmount,
            minWithdrawAmount: data.min_withdraw_amount || 0,
            maxWithdrawAmount: data.max_withdraw_amount || 0,
            hasWalletDeposit: data.has_transactions || false,
            withdrawEnabled: data.withdraw_enabled || 0,
            withdraw_enable_now: data.withdraw_enable_now || false,
            withdraw_next_date: data.withdraw_next_date || '',
          };

          const data_bonus = data?.bonus_wallet_result;

          const bonusCreditAmount =
            data_bonus.credit < 0 ? 0 : data_bonus.credit;

          if (data_bonus) {
            bonusWallet.value = {
              credit: bonusCreditAmount / 100,
              credit_hold: data_bonus.credit_hold / 100,
              sports_amount_hold: data_bonus.sports_amount_hold / 100,
              casino_amount_hold: data_bonus.casino_amount_hold / 100,
              expiry_datetime: data_bonus.expiry_datetime,
            };
          }

          const data_cashback = data?.cashback_wallet_result;

          const cashbackCreditAmount =
            data_cashback?.credit < 0 ? 0 : data_cashback?.credit;

          const cashbackExpiryDate = data_cashback?.expires_at || '';

          cashbackWallet.value = {
            credit: cashbackCreditAmount / 100,
            expires_at: cashbackExpiryDate,
          };

          isLoading.value = false;
          hasLoadedWallet.value = true;

          useOpenModalAfterTime().execute();
        })
        .catch((err: any) => {
          if (withCurrentUser) {
            userStore.setLoading(false);
          }
          console.error({ err });
          isLoading.value = false;
          // Marca como resolvida mesmo em erro: quem espera a carteira nao
          // pode ficar preso para sempre num fetch que falhou.
          hasLoadedWallet.value = true;
        });
    }
  }

  async function checkRollover() {
    return await ApiService.get('bonus/rollover-accomplished')
      .then((res: any) => {
        rolloverAccomplished.value = res.data.status || false;
      })
      .catch(() => {
        rolloverAccomplished.value = false;

        // Invalida a assinatura pra que o próximo `getRolloverData` tente de
        // novo mesmo com o rollover parado: sem isso, uma falha isolada aqui
        // deixaria o `rolloverAccomplished` preso em false até o jogador
        // apostar de novo.
        lastRolloverSignature = undefined;
      });
  }

  // Assinatura do rollover: os campos que fazem o `bonus/rollover-accomplished`
  // ter chance de responder diferente. Enquanto ela não muda, o jogador não
  // apostou e o veredito do cumprimento é o mesmo — repetir a segunda chamada
  // só dobra o volume do poll do jogo (ENG-817) sem mudar nada em tela.
  // `undefined` = ainda não buscamos nesta sessão, então a primeira resposta
  // sempre passa pelo `checkRollover`.
  //
  // A assinatura é chave de cache do VEREDITO, não da regra: só é gravada
  // quando o `checkRollover` de fato rodou pra ela. Quem chama com
  // `checkAccomplished: false` deixa a assinatura como estava, senão a
  // próxima chamada que PRECISA do veredito veria "não mudou" e pularia uma
  // conferência que nunca aconteceu.
  let lastRolloverSignature: string | undefined;

  function rolloverSignature(data: any): string {
    return JSON.stringify([
      data?.success,
      data?.amount,
      data?.count,
      data?.multiplier,
      data?.rollover_count,
    ]);
  }

  type GetRolloverDataOptions = {
    /**
     * Conferir tambem se o rollover foi CUMPRIDO (`bonus/rollover-accomplished`).
     *
     * `bonus/rollover` e `bonus/rollover-accomplished` sao dois fatos
     * diferentes do core: o primeiro traz a REGRA e o PROGRESSO, o segundo o
     * veredito booleano de cumprimento — que o front nao pode derivar do
     * progresso, porque a regra de cumprimento e do backoffice. Quem precisa
     * dos dois e a carteira de bonus (libera o saque do bonus em
     * `BonusWallet.vue`), e por isso o padrao e `true`.
     *
     * A faixa de rollover do header do jogo NAO le `rolloverAccomplished` —
     * so `amount` vs. `credit_hold * multiplier`. Pra ela a segunda ida ao
     * core e desperdicio puro, e justamente no caso mais comum: enquanto o
     * jogador gira, a assinatura muda a cada tick e o cache nao pega nada.
     * Passar `false` corta metade das requests do poll e, por tabela, a
     * latencia de cada ciclo — eram DUAS chamadas em serie.
     */
    checkAccomplished?: boolean;
  };

  async function getRolloverData({
    checkAccomplished = true,
  }: GetRolloverDataOptions = {}) {
    if (!userIsAuthenticated.value) {
      return;
    }

    try {
      const res: any = await ApiService.get('bonus/rollover');

      rollover.value = res.data;

      const signature = rolloverSignature(res.data);

      // Sem bonus com rollover ativo: nao ha veredito a buscar, e a resposta
      // do proprio `bonus/rollover` ja o determina. Grava a assinatura mesmo
      // com `checkAccomplished: false` porque aqui o veredito ESTA atualizado.
      if (res.data?.success === false) {
        rolloverAccomplished.value = false;
        lastRolloverSignature = signature;

        return;
      }

      if (!checkAccomplished) return;

      if (signature !== lastRolloverSignature) {
        lastRolloverSignature = signature;

        await checkRollover();
      }
    } catch {
      rolloverAccomplished.value = false;
    } finally {
      // Resolvida — inclusive em erro, senao quem espera a regra de rollover
      // para exibir fica preso num fetch que falhou. Mesmo contrato do
      // `hasLoadedWallet`.
      hasLoadedRollover.value = true;
    }
  }

  async function getTransactions(
    page: number,
    type: string,
    period: number | string = 'today',
  ) {
    transactions.value.data = [];

    if (isDev()) {
      await sleep(1000);

      const mockType = type.toLowerCase();

      const validTypes = [
        'deposit',
        'withdraw',
        'sports',
        'casino',
        'coupon',
        'bonus',
        'cashback',
        'clubevip',
      ];

      if (
        validTypes.includes(mockType) &&
        transactionsMock[mockType as keyof typeof transactionsMock]
      ) {
        const mockData = JSON.parse(
          JSON.stringify(
            transactionsMock[mockType as keyof typeof transactionsMock],
          ),
        ) as ITransactionResponse;

        return (transactions.value = mockData);
      }

      const defaultData = JSON.parse(
        JSON.stringify(transactionsMock.default),
      ) as ITransactionResponse;

      return (transactions.value = defaultData);
    }

    if (type.match(/cashback/gi)) {
      return await ApiService.get(
        `/transactions/cashback?page=${page}&date_period=${period}&per_page=10`,
      ).then((res) => {
        transactions.value = res.data;
      });
    }

    return await ApiService.post(`/bff/transactions`, {
      page,
      type,
      date_period: period,
    }).then((res: any) => {
      transactions.value = res.data;
    });
  }

  function setRealWalletCredit(value: number) {
    realWallet.value.credit = value;
  }

  function setBonusWalletCredit(value: number) {
    bonusWallet.value.credit = value;
  }

  const currentDepositMethod = ref<PaymentsMethodsType>(
    defaultSelectedDepositMethod.value,
  );

  const selectDepositMethod = (method: PaymentsMethodsType) => {
    currentDepositMethod.value = method;
  };

  const autoSubmitDeposit = ref(false);
  const autoSubmitDepositAmount = ref<string>('');

  function setAutoSubmitDeposit(amount: string) {
    autoSubmitDepositAmount.value = amount;
    autoSubmitDeposit.value = true;
  }

  function clearAutoSubmitDeposit() {
    autoSubmitDeposit.value = false;
    autoSubmitDepositAmount.value = '';
  }

  const currentWithdrawMethod = ref<PaymentsMethodsType>(
    defaultSelectedWithdrawMethod.value,
  );

  const selectWithdrawMethod = (method: PaymentsMethodsType) => {
    currentWithdrawMethod.value = method;
  };

  return {
    setRealWalletCredit,
    setBonusWalletCredit,
    realWallet,
    bonusWallet,
    cashbackWallet,
    transactions,
    isLoading,
    hasLoadedWallet,
    rolloverAccomplished,
    rollover,
    hasLoadedRollover,
    getWallet,
    checkRollover,
    getRolloverData,
    getTransactions,
    currentDepositMethod,
    selectDepositMethod,
    currentWithdrawMethod,
    selectWithdrawMethod,
    currentUserID,
    autoSubmitDeposit,
    autoSubmitDepositAmount,
    setAutoSubmitDeposit,
    clearAutoSubmitDeposit,
    $reset,
  };
});
